/**
 * Chat store — action surface.
 *
 * The `chatStore` object is the single entry point components use to mutate
 * conversation state: messages, streaming, costs, provider/model, attachments,
 * conversation persistence, summarize lifecycle. Hooks (`useChatStore`,
 * `useChatInput`, `useChatConversationList`) live in `chatStore.ts` and read
 * the state these actions write.
 *
 * Every mutator funnels through `setState` from `chatStore.internal` so the
 * snapshot/emit contract is enforced in exactly one place. Per-method state
 * derives via `getState()` so no method captures a stale `state` closure.
 *
 * Heavy sub-domains live in dedicated modules:
 *   • `chatStore.conversation.ts` — load/switch/archive/hydrate
 *   • `chatStore.summarize.ts`    — start/cancel/sync + poll loop
 *   • `chatStore.export.ts`       — export-to-JSON
 */
import { apiFetch } from './api';
import {
  archiveConversation,
  hydrateDefaults,
  loadConversations,
  reloadAfterSummary,
  loadEarlierMessages,
  restoreLastConversation,
  switchConversation,
  syncPendingQuestion,
} from './chatStore.conversation.js';
import { addError, addSystemNotice, setSummarizeJob } from './chatStore.mutations.js';
import { setLastConversationId } from './lastConversation.js';
import { exportCurrentConversation as exportToJSON } from './chatStore.export.js';
import {
  appendTextToSegments,
  bumpSwitchGeneration,
  getInputValue,
  getState,
  makeId,
  setInputValue,
  setState,
  setStateSilent,
  subscribe,
  withThrottledNotify,
} from './chatStore.internal.js';
import { cancelSummarize, startSummarize, stopSummarizePolling } from './chatStore.summarize.js';
import type { ChatAttachment, ChatMessage, SubAgentToolCall } from './chatStore.types.js';

export const chatStore = {
  getState,
  subscribe,
  withThrottledNotify,

  // ── Input ──────────────────────────────────────────────────────────────────

  getInput(): string {
    return getInputValue();
  },

  setInput(input: string): void {
    setInputValue(input);
  },

  // ── Messages ───────────────────────────────────────────────────────────────

  setMessages(messages: ChatMessage[]): void {
    setState({ ...getState(), messages });
  },

  dismissMessage(id: string): void {
    const s = getState();
    setState({ ...s, messages: s.messages.filter((m) => m.id !== id) });
  },

  /**
   * Append user + placeholder assistant message together.
   * Returns both IDs so the caller can stream text into the assistant slot.
   */
  appendUserAndAssistant(userContent: string, attachments?: ChatAttachment[]): { userId: string; assistantId: string } {
    const userId = makeId();
    const assistantId = makeId();
    const userMsg: ChatMessage = { id: userId, role: 'user', content: userContent };
    if (attachments && attachments.length > 0) userMsg.attachments = attachments;
    const assistantMsg: ChatMessage = { id: assistantId, role: 'assistant', segments: [] };
    const s = getState();
    setState({ ...s, messages: [...s.messages, userMsg, assistantMsg] });
    // Sending makes this thread the restore target — covers brand-new
    // conversations (persisted server-side on this first turn) that a plain
    // switch bookmark would miss.
    if (s.conversationId) setLastConversationId(s.conversationId);
    return { userId, assistantId };
  },

  appendTextToAssistant(assistantId: string, text: string): void {
    const s = getState();
    setState({
      ...s,
      messages: s.messages.map((m) =>
        m.id === assistantId && m.role === 'assistant'
          ? { ...m, segments: appendTextToSegments(m.segments ?? [], text) }
          : m,
      ),
    });
  },

  appendToolCallToAssistant(
    assistantId: string,
    args: { toolId: string; name: string; input: string; startedAt: number },
  ): void {
    const s = getState();
    setState({
      ...s,
      messages: s.messages.map((m) =>
        m.id === assistantId && m.role === 'assistant'
          ? {
              ...m,
              segments: [
                ...(m.segments ?? []),
                {
                  type: 'tool_call' as const,
                  id: args.toolId,
                  name: args.name,
                  input: args.input,
                  startedAt: args.startedAt,
                },
              ],
            }
          : m,
      ),
    });
  },

  /** Apply streamed tool input to the matching pending tool-call segment. */
  updateToolCallInput(assistantId: string, args: { toolId: string; input: string }): void {
    const s = getState();
    setState({
      ...s,
      messages: s.messages.map((m) => {
        if (m.id !== assistantId || m.role !== 'assistant') return m;
        let matched = false;
        const nextSegments = (m.segments ?? []).map((seg) => {
          if (matched || seg.type !== 'tool_call' || seg.id !== args.toolId || seg.output !== undefined) return seg;
          matched = true;
          return { ...seg, input: args.input };
        });
        return matched ? { ...m, segments: nextSegments } : m;
      }),
    });
  },

  appendToolResultToAssistant(
    assistantId: string,
    args: { toolId: string; output: string; durationMs: number; isError: boolean; isWarning?: boolean },
  ): void {
    const s = getState();
    setState({
      ...s,
      messages: s.messages.map((m) => {
        if (m.id !== assistantId || m.role !== 'assistant') return m;
        let matched = false;
        const nextSegments = (m.segments ?? []).map((seg) => {
          if (matched || seg.type !== 'tool_call' || seg.id !== args.toolId || seg.output !== undefined) return seg;
          matched = true;
          return {
            ...seg,
            output: args.output,
            durationMs: args.durationMs,
            isError: args.isError,
            ...(args.isWarning ? { isWarning: true } : {}),
          };
        });
        return matched ? { ...m, segments: nextSegments } : m;
      }),
    });
  },

  /**
   * Update the most recent pending spawn_agent tool call with sub-agent progress.
   * Called for every subagent SSE event — tool starts, tool results, text.
   */
  updateSubAgentProgress(
    assistantId: string,
    event: {
      subType: 'tool_start' | 'tool_input' | 'tool_result' | 'text';
      name?: string;
      id?: string;
      partialJson?: string;
      result?: string;
      isError?: boolean;
      text?: string;
    },
  ): void {
    const s = getState();
    setState({
      ...s,
      messages: s.messages.map((m) => {
        if (m.id !== assistantId || m.role !== 'assistant') return m;
        // Find the most recent pending spawn_agent tool call
        const segments = m.segments ?? [];
        let targetIdx = -1;
        for (let i = segments.length - 1; i >= 0; i--) {
          const seg = segments[i];
          if (
            seg &&
            seg.type === 'tool_call' &&
            (seg.name === 'spawn_agent' || seg.name === 'mcp__atlas__spawn_agent') &&
            seg.output === undefined
          ) {
            targetIdx = i;
            break;
          }
        }
        if (targetIdx === -1) return m;

        const seg = segments[targetIdx];
        if (!seg || seg.type !== 'tool_call') return m;
        const tools: SubAgentToolCall[] = [...(seg.subAgentTools ?? [])];

        if (event.subType === 'tool_start') {
          tools.push({
            id: event.id ?? '',
            name: event.name ?? 'tool',
            input: '',
          });
        } else if (event.subType === 'tool_input') {
          // Append to the matching tool's input
          for (let i = tools.length - 1; i >= 0; i--) {
            const t = tools[i];
            if (t && t.id === event.id && t.output === undefined) {
              tools[i] = { ...t, input: t.input + (event.partialJson ?? '') };
              break;
            }
          }
        } else if (event.subType === 'tool_result') {
          for (let i = tools.length - 1; i >= 0; i--) {
            const t = tools[i];
            if (t && t.id === event.id && t.output === undefined) {
              tools[i] = {
                ...t,
                output: event.result ?? '',
                ...(event.isError !== undefined ? { isError: event.isError } : {}),
              };
              break;
            }
          }
        }
        // 'text' events — we don't need to store sub-agent text in the segment
        // since it comes back in the tool result. No-op for now.

        const updatedSeg = { ...seg, subAgentTools: tools };
        const nextSegments = [...segments];
        nextSegments[targetIdx] = updatedSeg;
        return { ...m, segments: nextSegments };
      }),
    });
  },

  addError,
  addSystemNotice,

  /**
   * Remove a failed turn's user + assistant placeholders from the UI.
   * Call when the HTTP request failed before streaming started — nothing was
   * persisted server-side, so these messages are phantom UI state.
   */
  removeFailedTurn(userId: string, assistantId: string): void {
    const s = getState();
    setState({
      ...s,
      messages: s.messages.filter((m) => m.id !== userId && m.id !== assistantId),
    });
  },

  /**
   * Remove an empty assistant placeholder left by a turn that errored before
   * producing any content. Keeps the message if any segments or text arrived
   * (partial output is worth preserving). The "Thinking…" dots render for
   * an assistant message with no segments and no content — this cleans that up.
   */
  removeEmptyAssistant(assistantId: string): void {
    const s = getState();
    setState({
      ...s,
      messages: s.messages.filter((m) => {
        if (m.id !== assistantId) return true;
        // Keep if it received any content
        return (m.segments ?? []).length > 0 || !!m.content;
      }),
    });
  },

  // ── Streaming ──────────────────────────────────────────────────────────────

  setStreaming(isStreaming: boolean): void {
    setState({ ...getState(), isStreaming });
  },

  setStreamingDone(): void {
    setState({ ...getState(), isStreaming: false, abortController: null });
  },

  createAbortController(): AbortController {
    const controller = new AbortController();
    // Set silently — the caller is going to immediately start streaming and
    // emit further updates; bouncing subscribers here would be wasted work.
    setStateSilent({ ...getState(), abortController: controller });
    return controller;
  },

  stopStreaming(): void {
    const s = getState();
    s.abortController?.abort();
    const qs = s.conversationId ? `?sessionId=${encodeURIComponent(s.conversationId)}` : '';
    void apiFetch(`/api/chat/stop${qs}`, { method: 'POST' }).catch(() => {});
    // Clear any pending question card locally: the server cancels it too and
    // emits question_resolved, but this client just aborted its stream and
    // will never see that event — without this the dead card lingers.
    setState({ ...s, isStreaming: false, abortController: null, pendingQuestion: null });
  },

  // ── Turn completion ────────────────────────────────────────────────────

  /**
   * Consolidated end-of-turn action — replaces the former separate addTokenUsage,
   * setCost, and setConversationEstimate actions. Updates everything from the
   * single `turn_complete` SSE event.
   */
  applyTurnComplete(event: {
    usage: { inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheWriteTokens: number };
    cost: { totalCost: number };
    lastPromptTokens?: number;
    messageCount: number;
  }): void {
    const s = getState();
    setState({
      ...s,
      totalCost: s.totalCost + event.cost.totalCost,
      conversationMeta: {
        ...s.conversationMeta,
        messageCount: event.messageCount,
        ...(typeof event.lastPromptTokens === 'number' && event.lastPromptTokens > 0
          ? { lastPromptTokens: event.lastPromptTokens }
          : {}),
      },
    });
  },

  // ── Provider ───────────────────────────────────────────────────────────────

  /**
   * Apply the backend's routing decision for the active turn. The route event
   * is the single source of truth for what provider/model actually ran —
   * keeps the selector honest (a CLI conversation never displays as "API").
   * `reason` explains *why* this model ran; the selector flags it when the
   * route deviated from the user's pick (budget downgrade / failover / re-pin).
   * `effort` is what the model was sent ('' = its default).
   */
  applyRoute(provider: string, model: string, useExtendedContext: boolean, reason = '', effort = ''): void {
    if (!provider && !model) return;
    const s = getState();
    setState({
      ...s,
      ...(provider ? { provider } : {}),
      ...(model ? { model } : {}),
      useExtendedContext,
      routeReason: reason,
      routeEffort: effort,
    });
  },

  /** Store the current turn's injection metadata — what went into the agent's context. */
  setTurnMeta(meta: import('./chatStore.types.js').TurnMeta): void {
    setState({ ...getState(), turnMeta: meta });
  },

  // ── Operator questions (ask_operator) ─────────────────────────────────────

  /** Surface a mid-turn operator question — set by the `question` SSE event
   *  or the gap-reload snapshot. Renders the QuestionCard. */
  setPendingQuestion(q: import('./chatStore.types.js').PendingQuestion): void {
    setState({ ...getState(), pendingQuestion: q });
  },

  /** Remove the question card. When `id` is given, only clears if it matches
   *  the pending question (a late `question_resolved` for an older question
   *  must not dismiss a newer one). */
  clearPendingQuestion(id?: string): void {
    const s = getState();
    if (!s.pendingQuestion) return;
    if (id && s.pendingQuestion.id !== id) return;
    setState({ ...s, pendingQuestion: null });
  },

  /**
   * Resolve the pending question — POST /api/chat/answer. Optimistically
   * clears the card; a 409 (already resolved elsewhere: other device, timeout,
   * stop) surfaces as a non-fatal system notice. The agent's turn continues
   * either way — the outcome reaches it as the tool result.
   */
  async answerQuestion(action: 'accept' | 'decline', answer?: string): Promise<void> {
    const s = getState();
    const q = s.pendingQuestion;
    if (!q) return;
    setState({ ...s, pendingQuestion: null });
    try {
      const res = await apiFetch(`/api/chat/answer?sessionId=${encodeURIComponent(s.conversationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId: q.id, action, ...(answer !== undefined ? { answer } : {}) }),
      });
      if (!res.ok) {
        chatStore.addSystemNotice(
          res.status === 409
            ? 'That question was already resolved (answered elsewhere, timed out, or the turn stopped).'
            : `Failed to deliver the answer (${res.status}) — the agent will proceed without it.`,
        );
      }
    } catch {
      chatStore.addSystemNotice('Failed to deliver the answer (network) — the agent will proceed without it.');
    }
  },

  syncPendingQuestion,

  setProvider(provider: string, model: string, useExtendedContext = false): void {
    setState({ ...getState(), provider, model, useExtendedContext, explicitSelection: true });
  },

  // ── Attachments ───────────────────────────────────────────────────────────

  addPendingAttachment(attachment: ChatAttachment): void {
    const s = getState();
    setState({
      ...s,
      pendingAttachments: [...s.pendingAttachments, attachment],
      uploadError: null,
    });
  },

  removePendingAttachment(id: string): void {
    const s = getState();
    // Its local preview is shown nowhere else — a sent message's stays live with it.
    const removed = s.pendingAttachments.find((a) => a.id === id);
    if (removed?.localUrl) URL.revokeObjectURL(removed.localUrl);
    setState({
      ...s,
      pendingAttachments: s.pendingAttachments.filter((a) => a.id !== id),
    });
  },

  clearPendingAttachments(): void {
    setState({ ...getState(), pendingAttachments: [], uploadError: null });
  },

  setUploadError(error: string | null): void {
    setState({ ...getState(), uploadError: error });
  },

  // ── History dialog ────────────────────────────────────────────────────────

  setHistoryOpen(historyOpen: boolean): void {
    setState({ ...getState(), historyOpen });
  },

  // ── Summarize job — lifecycle lives in chatStore.summarize.ts ─────────────

  setSummarizeJob,
  startSummarize: (): Promise<void> => startSummarize(reloadAfterSummary),
  cancelSummarize,

  // ── Clear / Reset ─────────────────────────────────────────────────────────

  clearMessages(): void {
    chatStore.newConversation();
  },

  newConversation(): void {
    bumpSwitchGeneration(); // invalidate any in-flight conversation load
    stopSummarizePolling();
    setState({
      ...getState(),
      messages: [],
      earlier: null,
      conversationId: crypto.randomUUID(),
      totalCost: 0,
      summarizeJob: null,
      conversationMeta: {},
      explicitSelection: false,
      routeReason: '',
      routeEffort: '',
    });
  },

  // ── Export / Persistence — defined in their own modules ───────────────────

  exportCurrentConversation(): Promise<void> {
    return exportToJSON(getState());
  },

  loadConversations,
  switchConversation,
  loadEarlierMessages,
  archiveConversation,
  hydrateDefaults,
  restoreLastConversation,
};
